import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./BookingSummary.css";
import Footer from "../../Component/Footer/Footer";
import NavigationBar from "../../Component/NavigationBar/NavigationBar";
import CheckoutButton from "../../Component/CheckoutButton";

const BookingSummary = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchBookingId, setSearchBookingId] = useState("");
  const [bookingData, setBookingData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [cancellingBooking, setCancellingBooking] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [refundDetails, setRefundDetails] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Function to extract clean booking ID from URL parameter
  const getCleanBookingId = (id) => {
    if (!id) return null;
    const match = id.match(/^(\d+)/);
    return match ? match[1] : id;
  };

  // Improved date formatting function for ISO dates like "2025-10-14T12:47:13.959243+00:00Z"
  const formatDateTimeCorrectly = (dateString, timeString) => {
    console.log("🕒 Formatting:", { dateString, timeString });

    if (!dateString) return "Not available";

    try {
      // Handle ISO date strings with timezone information
      if (dateString && timeString) {
        const localDateTime = `${dateString}T${timeString}`;
        const date = new Date(localDateTime);

        if (isNaN(date.getTime())) {
          const fallbackDateTime = `${dateString} ${timeString}`;
          const fallbackDate = new Date(fallbackDateTime);
          if (!isNaN(fallbackDate.getTime())) {
            return fallbackDate.toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
          }
        }

        return date.toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }

      // Handle ISO datetime strings (like "2025-10-14T12:47:13.959243+00:00Z")
      if (typeof dateString === "string" && dateString.includes("T")) {
        // Clean up the string if it has multiple timezone indicators
        let cleanDateString = dateString;
        if (dateString.includes("+") && dateString.endsWith("Z")) {
          cleanDateString = dateString.replace("Z", "");
        }

        const date = new Date(cleanDateString);

        if (!isNaN(date.getTime())) {
          return date.toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // If we have a combined datetime string from database (like "2025-10-16 14:00:00")
      if (typeof dateString === "string" && dateString.includes(" ")) {
        const [datePart, timePart] = dateString.split(" ");
        const date = new Date(`${datePart}T${timePart}`);

        if (!isNaN(date.getTime())) {
          return date.toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Fallback - just date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }

      return dateString;
    } catch (error) {
      console.error("Date formatting error:", error);
      return `${dateString} ${timeString || ""}`;
    }
  };

  // Special function for booking date formatting
  const formatBookingDate = (dateString) => {
    if (!dateString) return "Not available";

    try {
      // Handle various date formats
      let date;

      if (dateString.includes("T")) {
        // ISO format like "2025-10-14T12:47:13.959243+00:00Z"
        let cleanDateString = dateString;
        if (dateString.includes("+") && dateString.endsWith("Z")) {
          cleanDateString = dateString.replace("Z", "");
        }
        date = new Date(cleanDateString);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        return "Invalid date";
      }

      // Format: "14 Oct 2025, 06:17 PM" (IST time)
      return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Booking date formatting error:", error);
      return dateString;
    }
  };

  const calculateTotalNights = (checkInDate, checkOutDate) => {
    if (!checkInDate || !checkOutDate) return "Not available";

    try {
      const checkIn = new Date(checkInDate);
      const checkOut = new Date(checkOutDate);
      const timeDiff = checkOut.getTime() - checkIn.getTime();
      const nights = Math.floor(timeDiff / (1000 * 3600 * 24));

      return nights > 0 ? nights : 1;
    } catch (error) {
      console.error("Error calculating nights:", error);
      return "Not available";
    }
  };

  const getTotalNights = (bookingData) => {
    if (!bookingData) return "Not available";

    const dateSources = [
      {
        checkIn: bookingData.stay?.checkIn?.date,
        checkOut: bookingData.stay?.checkOut?.date,
        source: "stay_object",
      },
      {
        checkIn: bookingData.check_in_date,
        checkOut: bookingData.check_out_date,
        source: "database_direct",
      },
      {
        checkIn: bookingData.result?.check_in_date,
        checkOut: bookingData.result?.check_out_date,
        source: "nested_result",
      },
    ];

    for (const source of dateSources) {
      if (source.checkIn && source.checkOut) {
        const nights = calculateTotalNights(source.checkIn, source.checkOut);
        if (nights !== "Not available") {
          return nights;
        }
      }
    }

    return "Not available";
  };

  // Function to calculate days until check-in
  // Function to calculate days until check-in
  const getDaysUntilCheckIn = () => {
    if (!bookingData?.stay?.checkIn?.date) return null;

    try {
      const checkInDate = new Date(bookingData.stay.checkIn.date);
      const today = new Date();

      // Reset both dates to midnight for accurate day comparison
      const checkInMidnight = new Date(
        checkInDate.getFullYear(),
        checkInDate.getMonth(),
        checkInDate.getDate()
      );
      const todayMidnight = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      const timeDiff = checkInMidnight.getTime() - todayMidnight.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

      if (daysDiff === 0) {
        return "today";
      } else if (daysDiff > 0) {
        return daysDiff;
      } else {
        return 0; // For past dates
      }
    } catch (error) {
      console.error("Error calculating days until check-in:", error);
      return null;
    }
  };

  // Show cancellation confirmation modal
  const handleCancelClick = () => {
    if (!bookingData) return;
    setShowCancelModal(true);
  };

  // Handle actual booking cancellation
  const handleCancelBooking = async () => {
    setShowCancelModal(false);
    setCancellingBooking(true);
    setCancelError(null);
    setCancelSuccess(false);

    try {
      const response = await fetch(
        `http://server.theartkothi.com/api/v1/bookings/${bookingData.booking_id}/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cancellation_reason: "Cancelled by customer",
          }),
        }
      );

      const result = await response.json();

      if (result.status) {
        setCancelSuccess(true);
        setRefundDetails(result.result.refund_details);

        // Refresh booking data to show updated status
        setTimeout(() => {
          fetchBookingSummary();
        }, 2000);
      } else {
        setCancelError(result.message || "Failed to cancel booking");
      }
    } catch (error) {
      console.error("Cancellation error:", error);
      setCancelError("Failed to cancel booking. Please try again.");
    } finally {
      setCancellingBooking(false);
    }
  };

  // Cashfree SDK loader (reuse from Book.jsx)
  const loadCashfreeSdk = async () => {
    if (window.Cashfree) return window.Cashfree;
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
      s.async = true;
      s.onload = resolve;
      s.onerror = () => reject(new Error("Failed to load Cashfree SDK"));
      document.head.appendChild(s);
    });
    return window.Cashfree;
  };

  const openCashfreeCheckout = async (paymentSessionId, mode = "sandbox") => {
    try {
      console.log("🔧 Loading Cashfree SDK...");

      const Cashfree = await loadCashfreeSdk();
      console.log("✅ Cashfree SDK loaded");

      const cf = Cashfree({ mode });
      console.log(
        "🔧 Initializing Cashfree checkout with session:",
        paymentSessionId
      );

      const result = await cf.checkout({
        paymentSessionId,
        redirectTarget: "_self",
      });

      console.log("✅ Cashfree checkout result:", result);

      if (result?.error) {
        console.error("❌ Cashfree error:", result.error);
        throw new Error(
          result.error?.message || "Payment initialization failed"
        );
      }

      console.log("🎉 Cashfree checkout opened successfully!");
    } catch (err) {
      console.error("💥 Cashfree checkout error:", err);
      throw err;
    }
  };

  // Payment completion handler
  const handleCompletePayment = async () => {
    if (!bookingData) return;

    setProcessingPayment(true);
    setPaymentError(null);

    try {
      console.log(
        "🔄 Starting payment retry for booking:",
        bookingData.booking_id
      );

      const response = await fetch(
        "http://server.theartkothi.com/api/v1/bookings/retry-payment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            booking_id: bookingData.booking_id,
          }),
        }
      );

      console.log("📨 Response status:", response.status);

      const result = await response.json();
      console.log("📦 Full response:", result);

      if (!response.ok || !result.status) {
        console.error("❌ Backend error:", result.message);
        throw new Error(result.message || "Failed to create payment session");
      }

      console.log("✅ Backend success, checking for payment session...");

      const paymentSessionId = result.result?.payment_session_id;
      console.log("🎫 Payment session ID:", paymentSessionId);

      if (!paymentSessionId) {
        console.error("❌ No payment session in result:", result);
        throw new Error("No payment session received from server");
      }

      console.log("🚀 Opening Cashfree checkout...");

      // Open Cashfree checkout
      await openCashfreeCheckout(paymentSessionId, "sandbox");

      console.log("✅ Cashfree checkout opened successfully");
    } catch (err) {
      console.error("💥 Payment error:", err);
      setPaymentError(err.message || "Payment initialization failed");
    } finally {
      setProcessingPayment(false);
    }
  };

  const getCorrectPaymentAmount = () => {
    // Priority 1: Use the actual total_amount from booking data (most reliable)
    if (bookingData?.pricing?.subtotal) {
      return bookingData.pricing.subtotal;
    }

    // Fallback to total_amount if subtotal is not available
    if (bookingData?.total_amount) {
      return bookingData.total_amount;
    }

    // Fallback to nested result
    if (bookingData?.result?.total_amount) {
      return bookingData.result.total_amount;
    }

    return "Amount";
  };

  const debugPaymentAmounts = () => {
    console.log("💰 Payment Amount Debug:", {
      total_amount: bookingData?.total_amount,
      nested_total: bookingData?.result?.total_amount,
      pricing_total: bookingData?.pricing?.total,
      pricing_subtotal: bookingData?.pricing?.subtotal,
      booking_status: bookingData?.booking_status,
      booking_id: bookingData?.booking_id,
    });
  };

  // Call it when booking data loads
  useEffect(() => {
    if (bookingData) {
      debugPaymentAmounts();
    }
  }, [bookingData]);

  const fetchBookingSummary = async () => {
    const cleanBookingId = getCleanBookingId(bookingId);
    if (!cleanBookingId) {
      setError("Please enter a valid booking ID");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `http://server.theartkothi.com/api/v1/bookings/${cleanBookingId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("📦 Booking API Response:", result);

        if (result.status && result.result) {
          setBookingData(result.result);
          setError(null);
        } else {
          setError(
            `Booking ID "${cleanBookingId}" not found. Please check the ID and try again.`
          );
        }
      } else if (response.status === 404) {
        setError(
          `Booking ID "${cleanBookingId}" not found. Please verify the booking ID and try again.`
        );
      } else {
        setError("Unable to load booking details. Please try again later.");
      }
    } catch (error) {
      console.error("Error fetching booking summary:", error);
      if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        setError(
          "Network error. Please check your internet connection and try again."
        );
      } else {
        setError(
          "An error occurred while fetching booking details. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserDetails = async (customerId) => {
    if (!customerId) return;

    try {
      const response = await fetch(
        `http://server.theartkothi.com/api/v1/users/${customerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.status && result.result) {
          setUserData(result.result);
        }
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  useEffect(() => {
    if (bookingId) {
      fetchBookingSummary();
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingData) {
      const customerId =
        bookingData.customer_id || bookingData.result?.customer_id;
      if (customerId) {
        fetchUserDetails(customerId);
      }
    }
  }, [bookingData]);

  const handlePrint = () => {
    window.print();
  };

  const handleGoHome = () => {
    navigate("/Homepage");
  };

  const handleNewBooking = () => {
    navigate("/book");
  };

  // Refresh booking data after payment
  const refreshBookingData = () => {
    fetchBookingSummary();
  };

  if (isLoading) {
    return (
      <div className="booking-summary-page">
        <NavigationBar />
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading booking summary...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-summary-page">
        <NavigationBar />
        <div className="error-container">
          <i className="fas fa-exclamation-triangle"></i>
          <h3>Booking Not Found</h3>
          <p>{error}</p>

          {/* Search again section - EXACT COPY from Book.jsx */}
          <div
            className="search-booking-bar"
            style={{
              marginTop: "2rem",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (searchBookingId.trim()) {
                  navigate(`/booking/${searchBookingId.trim()}`);
                }
              }}
              style={{ display: "flex", gap: "0.5rem" }}
            >
              <input
                type="text"
                placeholder="Enter Booking ID"
                value={searchBookingId}
                onChange={(e) => setSearchBookingId(e.target.value)}
                style={{
                  padding: "0.5rem 1rem",
                  border: "2px solid #e1e8ed",
                  borderRadius: "25px",
                  outline: "none",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#e2edf5ff";
                  e.target.style.width = "250px";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e1e8ed";
                  e.target.style.width = "200px";
                }}
              />
              <button
                type="submit"
                style={{
                  padding: "0.5rem 1rem",
                  background: "#3d2817",
                  color: "white",
                  border: "none",
                  borderRadius: "25px",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                }}
                onMouseOver={(e) => (e.target.style.background = "#8f592dff")}
                onMouseOut={(e) => (e.target.style.background = "#8f592dff")}
              >
                Search
              </button>
            </form>
          </div>

          <div className="action-buttons" style={{ marginTop: "2rem" }}>
            <button onClick={() => navigate("/book")} className="btn">
              Create New Booking
            </button>
            <button onClick={handleGoHome} className="btn">
              Go to Homepage
            </button>
          </div>

          {/* Help text */}
          <div
            className="help-text"
            style={{ marginTop: "1.5rem", fontSize: "0.9rem", color: "#666" }}
          >
            <p>
              <strong>Need help?</strong>
            </p>
            <p> Double-check your booking ID for typos</p>
            <p> Contact support at booking@theartkothi.com</p>
            <p> Call us at +91 98871 70770</p>
          </div>
        </div>
      </div>
    );
  }

  const isPending = bookingData?.booking_status === "PENDING";
  const isConfirmed = bookingData?.booking_status === "CONFIRMED";
  const isCancelled = bookingData?.booking_status === "CANCELLED";

  return (
    <div className="booking-summary-page">
      <NavigationBar />

      {/* Header with Status */}
      <div
        className={`summary-header ${
          isPending
            ? "pending-header"
            : isConfirmed
            ? "confirmed-header"
            : "cancelled-header"
        }`}
      >
        <div className="container">
          <h1 className="luxury-title">The Art Kothi</h1>
          <div className="booking-status-banner">
            <div
              className={`status-indicator ${
                isPending ? "pending" : isConfirmed ? "confirmed" : "cancelled"
              }`}
            >
              <i
                className={`fas ${
                  isPending
                    ? "fa-clock"
                    : isConfirmed
                    ? "fa-check-circle"
                    : "fa-times-circle"
                } me-2`}
              ></i>
              {isPending
                ? "Payment Pending"
                : isConfirmed
                ? "Booking Confirmed"
                : "Booking Cancelled"}
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="booking-summary-card">
          {/* Action Buttons */}
          <div className="summary-actions">
            {isPending && (
              <button
                onClick={handleCompletePayment}
                disabled={processingPayment}
                className="btn-payment"
              >
                {processingPayment ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-credit-card me-2"></i>
                    Complete Payment - ₹{getCorrectPaymentAmount()}
                  </>
                )}
              </button>
            )}

            <button onClick={handleNewBooking} className="btn">
              <i className="fas fa-plus me-2"></i>
              New Booking
            </button>
            <button onClick={handleGoHome} className="btn">
              <i className="fas fa-home me-2"></i>
              Home
            </button>
            <button onClick={handlePrint} className="btn">
              <i className="fas fa-print me-2"></i>
              Print
            </button>
          </div>

          {/* Cancellation Success Message */}
          {cancelSuccess && (
            <div
              className="confirmation-message"
              style={{ background: "#d4edda", borderColor: "#c3e6cb" }}
            >
              <i className="fas fa-check-circle"></i>
              <h3>Booking Cancelled Successfully!</h3>
              <p>Your booking has been cancelled successfully.</p>
              {refundDetails && (
                <div className="confirmation-details">
                  <p>
                    <strong>Refund Details:</strong>
                  </p>
                  <p>Refund Amount: ₹{refundDetails.refund_amount}</p>
                  <p>Refund Percentage: {refundDetails.refund_percentage}%</p>

                  {/* ✅ NEW: Cashfree Refund Status */}
                  {refundDetails.cashfree_refund_initiated && (
                    <div
                      style={{
                        marginTop: "1rem",
                        padding: "0.75rem",
                        background: "#fff3cd",
                        borderRadius: "8px",
                        border: "1px solid #ffc107",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 0.5rem 0",
                          fontWeight: "bold",
                          color: "#856404",
                        }}
                      >
                        <i className="fas fa-info-circle me-2"></i>
                        Refund Processing
                      </p>
                      <p
                        style={{
                          margin: "0 0 0.25rem 0",
                          fontSize: "0.9rem",
                          color: "#856404",
                        }}
                      >
                        ✓ Refund has been initiated with Cashfree
                      </p>
                      <p
                        style={{
                          margin: "0 0 0.25rem 0",
                          fontSize: "0.9rem",
                          color: "#856404",
                        }}
                      >
                        ✓ Money will be credited to your original payment method
                      </p>
                      <p
                        style={{
                          margin: "0",
                          fontSize: "0.9rem",
                          color: "#856404",
                        }}
                      >
                        ✓ Expected in 5-7 business days
                      </p>
                      {refundDetails.cashfree_refund_id && (
                        <p
                          style={{
                            margin: "0.5rem 0 0 0",
                            fontSize: "0.85rem",
                            color: "#666",
                            fontFamily: "monospace",
                          }}
                        >
                          Refund ID: {refundDetails.cashfree_refund_id}
                        </p>
                      )}
                    </div>
                  )}

                  {/* ✅ NEW: Manual Refund Required Warning */}
                  {refundDetails.manual_action_required && (
                    <div
                      style={{
                        marginTop: "1rem",
                        padding: "0.75rem",
                        background: "#f8d7da",
                        borderRadius: "8px",
                        border: "1px solid #f5c6cb",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 0.5rem 0",
                          fontWeight: "bold",
                          color: "#721c24",
                        }}
                      >
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Manual Refund Required
                      </p>
                      <p
                        style={{
                          margin: "0",
                          fontSize: "0.9rem",
                          color: "#721c24",
                        }}
                      >
                        {refundDetails.support_message}
                      </p>
                      <p
                        style={{
                          margin: "0.5rem 0 0 0",
                          fontSize: "0.9rem",
                          color: "#721c24",
                        }}
                      >
                        📞 Contact: +91 98871 70770 | 📧 booking@theartkothi.com
                      </p>
                    </div>
                  )}

                  {!refundDetails.cashfree_refund_initiated &&
                    !refundDetails.manual_action_required &&
                    refundDetails.refund_amount > 0 && (
                      <p
                        style={{
                          marginTop: "0.5rem",
                          color: "#666",
                          fontSize: "0.9rem",
                        }}
                      >
                        Refund will be processed within 5-7 business days.
                      </p>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Payment Error */}
          {paymentError && (
            <div className="payment-error-message">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {paymentError}
              <button
                onClick={() => setPaymentError(null)}
                className="close-error"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}

          {/* Cancellation Error */}
          {cancelError && (
            <div className="payment-error-message">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {cancelError}
              <button
                onClick={() => setCancelError(null)}
                className="close-error"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}

          {/* Pending Payment Notice */}
          {isPending && (
            <div className="pending-notice">
              <div className="notice-header">
                <i className="fas fa-info-circle me-2"></i>
                <h3>Payment Required</h3>
              </div>
              <p>
                Your booking is currently pending payment. To confirm your
                reservation, please complete the payment by clicking the
                "Complete Payment" button above. Your room will be held for you
                until{" "}
                {formatDateTimeCorrectly(
                  bookingData.stay?.checkIn?.date,
                  "14:00"
                )}
                .
              </p>
              <div className="payment-details">
                <div className="payment-methods">
                  <i className="fas fa-credit-card me-1"></i>
                  Accepts: UPI, Credit/Debit Cards, Net Banking
                </div>
              </div>
            </div>
          )}

          {bookingData && (
            <>
              {/* Booking Reference */}
              <div className="summary-section">
                <h2 className="section-title">
                  <i className="fas fa-tag me-2"></i>
                  Booking Reference
                </h2>
                <div className="summary-details">
                  <div className="summary-row">
                    <span className="summary-label">Booking ID:</span>
                    <span className="summary-value">
                      #
                      {bookingData.booking_listing_id || bookingData.booking_id}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Status:</span>
                    <span
                      className={`summary-value status-${bookingData.booking_status?.toLowerCase()}`}
                    >
                      {bookingData.booking_status}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Booking Date:</span>
                    <span className="summary-value">
                      {formatBookingDate(
                        bookingData.bookingDate || bookingData.created_at
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Hotel Information */}
              <div className="summary-section">
                <h2 className="section-title">
                  <i className="fas fa-hotel me-2"></i>
                  Hotel Information
                </h2>
                <div className="summary-details">
                  <div className="summary-row">
                    <span className="summary-label">Hotel Name:</span>
                    <span className="summary-value">
                      {bookingData.hotel?.name || "The Art Kothi"}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Location:</span>
                    <span className="summary-value">
                      {bookingData.hotel?.location || "Rajasthan, India"}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Contact:</span>
                    <span className="summary-value">
                      {bookingData.contactInfo?.hotelContact?.phone ||
                        "+91 98871 70770"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stay Duration */}
              <div className="summary-section">
                <h2 className="section-title">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Stay Duration
                </h2>
                <div className="summary-details">
                  <div className="summary-row">
                    <span className="summary-label">Check-in:</span>
                    <span className="summary-value">
                      {formatDateTimeCorrectly(
                        bookingData.stay?.checkIn?.date,
                        bookingData.stay?.checkIn?.time
                      )}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Check-out:</span>
                    <span className="summary-value">
                      {formatDateTimeCorrectly(
                        bookingData.stay?.checkOut?.date,
                        bookingData.stay?.checkOut?.time
                      )}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Total Nights:</span>
                    <span className="summary-value">
                      {getTotalNights(bookingData)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Guest Information */}
              <div className="summary-section">
                <h2 className="section-title">
                  <i className="fas fa-users me-2"></i>
                  Guest Information
                </h2>
                <div className="summary-details">
                  <div className="summary-row">
                    <span className="summary-label">Total Guests:</span>
                    <span className="summary-value">
                      {bookingData.guests?.total || 0}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Adults:</span>
                    <span className="summary-value">
                      {bookingData.guests?.adults || 0}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Children:</span>
                    <span className="summary-value">
                      {bookingData.guests?.children || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="summary-section">
                <h2 className="section-title">
                  <i className="fas fa-user me-2"></i>
                  Contact Information
                </h2>
                <div className="summary-details">
                  <div className="summary-row">
                    <span className="summary-label">Name:</span>
                    <span className="summary-value">
                      {userData
                        ? `${userData.first_name} ${userData.last_name}`
                        : bookingData.contactInfo?.primaryContact?.name ||
                          "Loading user details..."}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Phone:</span>
                    <span className="summary-value">
                      {userData?.phone ||
                        bookingData.contactInfo?.primaryContact?.phone ||
                        "Not available"}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Email:</span>
                    <span className="summary-value">
                      {userData?.email ||
                        bookingData.contactInfo?.primaryContact?.email ||
                        "Not available"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Pricing Details */}
              <div className="summary-section">
                <h2 className="section-title">
                  <i className="fas fa-rupee-sign me-2"></i>
                  Pricing Details
                </h2>
                <div className="summary-details">
                  <div className="summary-row">
                    <span className="summary-label">Total:</span>
                    <span className="summary-value">
                      ₹{bookingData.pricing?.subtotal || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Confirmation Message */}
              {isConfirmed && (
                <div className="confirmation-message">
                  <i className="fas fa-check-circle"></i>
                  <h3>Booking Confirmed!</h3>
                  <p>
                    Thank you for your payment. Your booking has been confirmed.
                    You will receive a confirmation email shortly with all the
                    details.
                  </p>
                  <div className="confirmation-details">
                    <p>
                      <strong>Next Steps:</strong>
                      <br />
                      1. Present this confirmation at check-in
                      <br />
                      2. Keep your ID proof ready for verification
                      <br />
                      3. Check-in time: 12:00 PM | Check-out: 11:00 AM
                    </p>
                  </div>
                </div>
              )}

              {isConfirmed && bookingData.booking_qrcode && (
                <div className="summary-section">
                  <h2 className="section-title">
                    <i className="fas fa-qrcode me-2"></i>
                    Booking QR Code
                  </h2>
                  <div className="qr-code-container">
                    <img
                      src={bookingData.booking_qrcode}
                      alt="Booking QR Code"
                      className="qr-code-image"
                    />
                    <p className="qr-code-note">
                      Scan this QR code at check-in for quick verification
                    </p>
                  </div>
                </div>
              )}
              {isConfirmed && !bookingData.booking_qrcode && (
                <div className="summary-section">
                  <h2 className="section-title">
                    <i className="fas fa-qrcode me-2"></i>
                    Booking QR Code
                  </h2>
                  <div className="qr-code-placeholder">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>QR code is being generated...</p>
                    <button
                      onClick={refreshBookingData}
                      className="btn-refresh"
                    >
                      <i className="fas fa-sync-alt me-2"></i>
                      Refresh to Check QR Code
                    </button>
                  </div>
                </div>
              )}
              {/* Cancellation Policy Information */}
              {/* Cancellation Policy Information */}
              {isConfirmed && (
                <div className="cancellation-policy">
                  <h4 style={{ color: "#6c757d", marginBottom: "1rem" }}>
                    <i className="fas fa-info-circle me-2"></i>
                    Cancellation Policy
                  </h4>
                  <ul
                    style={{
                      color: "#6c757d",
                      margin: 0,
                      paddingLeft: "1.5rem",
                    }}
                  >
                    <li>More than 7 days before check-in: 90% refund</li>
                    <li>1-7 days before check-in: 70% refund</li>
                    <li>Less than 24 hours before check-in: 50% refund</li>
                  </ul>
                  {getDaysUntilCheckIn() !== null && (
                    <p
                      style={{
                        marginTop: "1rem",
                        fontWeight: "600",
                        color: "#495057",
                      }}
                    >
                      Your check-in is{" "}
                      {getDaysUntilCheckIn() === "today"
                        ? "today"
                        : `in ${getDaysUntilCheckIn()} days`}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          <div className="action-buttons-right">
            <CheckoutButton
              bookingId={bookingData?.booking_id}
              checkOutDate={
                bookingData?.stay?.checkOut?.date || bookingData?.check_out_date
              }
              bookingStatus={bookingData?.booking_status}
            />
            {isConfirmed && (
              <button
                onClick={handleCancelClick}
                disabled={cancellingBooking}
                className="btn-cancel"
              >
                {cancellingBooking ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    Cancelling...
                  </>
                ) : (
                  <>Cancel Booking</>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Cancellation Confirmation Modal */}
        {showCancelModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Cancel Booking
                </h3>
                <button
                  className="modal-close"
                  onClick={() => setShowCancelModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-body">
                <div className="warning-icon">
                  <i className="fas fa-exclamation-circle"></i>
                </div>
                <h4>Are you sure you want to cancel this booking?</h4>
                <p>This action cannot be undone. Your booking details:</p>

                <div className="booking-details-preview">
                  <div className="detail-item">
                    <span className="label">Booking ID:</span>
                    <span className="value">
                      #
                      {bookingData.booking_listing_id || bookingData.booking_id}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Check-in:</span>
                    <span className="value">
                      {formatDateTimeCorrectly(
                        bookingData.stay?.checkIn?.date,
                        bookingData.stay?.checkIn?.time
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Amount:</span>
                    <span className="value">₹{getCorrectPaymentAmount()}</span>
                  </div>
                </div>

                {getDaysUntilCheckIn() !== null && (
                  <div className="refund-notice">
                    <i className="fas fa-info-circle me-2"></i>
                    <span>
                      Based on our cancellation policy, you may be eligible for
                      a{" "}
                      {getDaysUntilCheckIn() === "today" ||
                      getDaysUntilCheckIn() === 0
                        ? "50%" // Less than 24 hours
                        : getDaysUntilCheckIn() >= 1 &&
                          getDaysUntilCheckIn() <= 7
                        ? "70%" // 1-7 days
                        : getDaysUntilCheckIn() > 7
                        ? "90%" // More than 7 days
                        : "0%"}{" "}
                      {/* Fallback */} refund.
                    </span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => setShowCancelModal(false)}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Keep Booking
                </button>
                <button
                  className="btn-danger"
                  onClick={handleCancelBooking}
                  disabled={cancellingBooking}
                >
                  {cancellingBooking ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-times me-2"></i>
                      Yes, Cancel Booking
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default BookingSummary;
